import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { db } from "../../firebase";
import { useAuth } from "../../auth/AuthContext";
import { parseQuiltUri } from "../quiltGraph/quiltUri";

function normalizeBlocks(value) {
  if (!Array.isArray(value)) return [[{ kind: "text", text: "" }]];
  const normalized = value
    .map((block) => {
      if (!Array.isArray(block)) return [];
      return block.filter((span) => span && (span.kind === "text" || span.kind === "ref"));
    })
    .filter((block) => block.length > 0);
  return normalized.length ? normalized : [[{ kind: "text", text: "" }]];
}

export default function DocEditorPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [title, setTitle] = useState("");
  const [blocks, setBlocks] = useState([[{ kind: "text", text: "" }]]);
  const [status, setStatus] = useState("");
  const [insertText, setInsertText] = useState("");
  const [nodeSearch, setNodeSearch] = useState("");
  const [selectedNodeId, setSelectedNodeId] = useState("");
  const [nodes, setNodes] = useState([]);

  useEffect(() => {
    if (!user?.uid || !docId) return undefined;
    const unsub = onSnapshot(doc(db, "docs", docId), (snap) => {
      if (!snap.exists()) {
        setStatus("Doc not found.");
        return;
      }
      const data = snap.data();
      if (data.ownerUid !== user.uid) {
        setStatus("Access denied.");
        return;
      }
      setTitle(data.title || "Untitled");
      setBlocks(normalizeBlocks(data.blocks));
      setStatus("Loaded");
    }, (error) => setStatus(error.message || "Failed to load doc"));

    return () => unsub();
  }, [user?.uid, docId]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const q = query(collection(db, "graphs", "default", "nodes"), where("ownerUid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((row) => ({ id: row.id, ...row.data() }));
      setNodes(rows);
    });
  }, [user?.uid]);

  const filteredNodes = useMemo(() => {
    const lower = nodeSearch.trim().toLowerCase();
    return nodes.filter((node) => !lower || (node.label || "").toLowerCase().includes(lower));
  }, [nodes, nodeSearch]);

  const saveDoc = async (nextTitle = title, nextBlocks = blocks) => {
    if (!user?.uid || !docId) return;
    await setDoc(doc(db, "docs", docId), {
      title: nextTitle || "Untitled",
      ownerUid: user.uid,
      blocks: normalizeBlocks(nextBlocks),
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    }, { merge: true });
    setStatus("Saved");
  };

  const appendSpan = async (span, blockIndex = 0) => {
    const next = [...blocks];
    const block = Array.isArray(next[blockIndex]) ? [...next[blockIndex]] : [];
    block.push(span);
    next[blockIndex] = block;
    setBlocks(next);
    await saveDoc(title, next);
  };

  const handlePasteUri = async (event) => {
    const text = event.clipboardData?.getData("text") || "";
    const parsed = parseQuiltUri(text);
    if (!parsed) return;
    event.preventDefault();
    const label = nodes.find((node) => node.id === parsed.id)?.label || parsed.id;
    await appendSpan({
      kind: "ref",
      refType: parsed.refType,
      id: parsed.id,
      label,
    });
    setStatus(`Inserted ${parsed.refType} ref span from URI`);
  };

  const addBlock = async () => {
    const next = [...blocks, [{ kind: "text", text: "" }]];
    setBlocks(next);
    await saveDoc(title, next);
  };

  if (!user) {
    return <Box sx={{ p: 3 }}><Typography>Sign in to edit docs.</Typography></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button component={Link} to="/docs" variant="outlined">Back</Button>
          <Typography variant="h5" fontWeight={800}>Doc Editor</Typography>
          <Button variant="contained" onClick={() => void saveDoc()}>Save</Button>
        </Stack>

        <TextField
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={(event) => void saveDoc(event.target.value, blocks)}
        />

        {!!status && <Typography variant="body2" color="text.secondary">{status}</Typography>}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.2}>
            <Typography fontWeight={700}>Structured spans</Typography>
            {blocks.map((block, idx) => (
              <Box key={`block-${idx}`} sx={{ p: 1, borderRadius: 1, border: "1px solid #dbeafe", minHeight: 42 }}>
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                  {block.map((span, spanIdx) => {
                    if (span.kind === "text") {
                      return <Typography key={`span-${idx}-${spanIdx}`}>{span.text}</Typography>;
                    }
                    const path = span.refType === "edge" ? `/q/edge/${span.id}` : `/q/node/${span.id}`;
                    return (
                      <Chip
                        key={`span-${idx}-${spanIdx}`}
                        label={span.label || `${span.refType}:${span.id}`}
                        color="primary"
                        variant="outlined"
                        onClick={() => navigate(path)}
                      />
                    );
                  })}
                </Stack>
              </Box>
            ))}
            <Button onClick={() => void addBlock()}>New block</Button>
          </Stack>
        </Paper>

        <Divider />

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.2}>
            <Typography fontWeight={700}>Insert spans (MVP appends to block 1)</Typography>
            <TextField
              label="Add text span"
              value={insertText}
              onChange={(event) => setInsertText(event.target.value)}
              onPaste={handlePasteUri}
              helperText="Pasting quilt://node/<id> or quilt://edge/<id> inserts a ref span."
            />
            <Button
              variant="outlined"
              onClick={() => {
                if (!insertText.trim()) return;
                void appendSpan({ kind: "text", text: insertText.trim() });
                setInsertText("");
              }}
            >
              Insert text span
            </Button>

            <TextField
              label="Search nodes"
              value={nodeSearch}
              onChange={(event) => setNodeSearch(event.target.value)}
              onPaste={handlePasteUri}
            />
            <TextField
              select
              label="Insert Node Ref"
              value={selectedNodeId}
              onChange={(event) => setSelectedNodeId(event.target.value)}
            >
              <MenuItem value="">Select node</MenuItem>
              {filteredNodes.slice(0, 120).map((node) => (
                <MenuItem key={node.id} value={node.id}>{node.label || node.id}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              disabled={!selectedNodeId}
              onClick={() => {
                const node = nodes.find((item) => item.id === selectedNodeId);
                if (!node) return;
                void appendSpan({
                  kind: "ref",
                  refType: "node",
                  id: node.id,
                  label: node.label || node.id,
                });
                setSelectedNodeId("");
              }}
            >
              Insert Node Ref
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
